import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Services.css";
import doctorImage from "../assets/doctor-image.jpg";

const Services = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    { 
      name: "Individual Counselling", 
      icon: "bi-person", 
      description: "Personalized therapy for anxiety, depression, trauma, and personal growth.",
      image: "individual.jpg",
      details: {
        overview: "Professional one-on-one counseling tailored to your needs, helping you overcome challenges and achieve personal growth.",
        focusAreas: [
          "Anxiety and stress management",
          "Depression and mood disorders",
          "Personal growth and self-discovery",
          "Trauma and PTSD recovery",
          "Life transitions and adjustment"
        ],
        approach: "We use evidence-based approaches like Cognitive Behavioral Therapy (CBT) and Person-Centered therapy."
      }
    },
    { 
      name: "Staff Training & Corporate Wellness", 
      icon: "bi-people", 
      description: "Mental health training for corporate and educational organizations.",
      image: "staff-training.jpg",
      details: {
        overview: "Comprehensive staff training and corporate wellness programs designed to equip organizations with essential mental health awareness and stress management techniques.",
        focusAreas: [
          "Mental health first aid certification",
          "Workplace stress management",
          "Early signs recognition",
          "Professional communication strategies",
          "Supportive work environment development"
        ],
        approach: "Interactive workshops, case studies, and practical skill-building for corporate and educational settings."
      }
    },
    { 
      name: "Premarital Counselling", 
      icon: "bi-heart", 
      description: "Counseling for couples preparing for marriage with evidence-based frameworks.",
      image: "premarital.jpg",
      details: {
        overview: "Expert premarital counseling helps couples build strong foundations for successful marriages and address potential challenges.",
        focusAreas: [
          "Communication skills enhancement",
          "Conflict resolution strategies",
          "Financial planning management",
          "Family dynamics and boundaries",
          "Intimacy and relationship planning"
        ],
        approach: "Gottman Method Couples Therapy combined with practical relationship exercises."
      }
    },
    { 
      name: "Couples & Marriage Therapy", 
      icon: "bi-hearts", 
      description: "Improve communication, rebuild trust, and strengthen relationships.",
      image: "couples.jpg",
      details: {
        overview: "Professional counseling to help couples address conflicts and rebuild emotional connections.",
        focusAreas: [
          "Communication improvement",
          "Trust rebuilding",
          "Conflict resolution",
          "Intimacy enhancement",
          "Relationship goal setting"
        ],
        approach: "Integrative approach combining Emotionally Focused Therapy with practical tools."
      }
    },
    { 
      name: "Child & Adolescent Counselling", 
      icon: "bi-emoji-smile", 
      description: "Specialized support for children and teenagers navigating emotional challenges.",
      image: "child.jpg",
      details: {
        overview: "Age-appropriate therapy to help children and adolescents express emotions and develop coping skills.",
        focusAreas: [
          "Behavioral issues management",
          "School-related stress",
          "Social skills development",
          "Family adjustment support",
          "Emotional regulation"
        ],
        approach: "Play therapy, art therapy, and cognitive-behavioral techniques for different age groups."
      }
    },
    { 
      name: "Family Counselling", 
      icon: "bi-house-heart", 
      description: "Strengthen family bonds and improve communication dynamics.",
      image: "family.jpg",
      details: {
        overview: "Address family patterns to create healthier dynamics and stronger connections.",
        focusAreas: [
          "Parent-child relationships",
          "Sibling conflict resolution",
          "Blended family adjustment",
          "Communication patterns",
          "Family boundary setting"
        ],
        approach: "Family systems theory focusing on dynamics and intergenerational patterns."
      }
    },
    { 
      name: "Group Therapy & Support Sessions", 
      icon: "bi-collection", 
      description: "Therapeutic support sessions for shared growth experiences.",
      image: "group.jpg",
      details: {
        overview: "Supportive therapeutic communities where individuals can share experiences and learn from others.",
        focusAreas: [
          "Social anxiety treatment",
          "Grief and loss support",
          "Anger management skills",
          "Social skills enhancement",
          "Life challenge communities"
        ],
        approach: "Process-oriented therapeutic groups with structured activities and peer support."
      }
    },
    { 
      name: "Geriatric Counselling", 
      icon: "bi-person-walking", 
      description: "Mental health support for seniors and elderly counseling services.",
      image: "geriatric.jpg",
      details: {
        overview: "Professional counseling addressing the unique mental health needs of older adults.",
        focusAreas: [
          "Aging and life transitions",
          "Grief processing",
          "Cognitive health concerns",
          "Family role adjustments",
          "End-of-life planning"
        ],
        approach: "Life review therapy and supportive counseling for aging-related challenges."
      }
    },
    { 
      name: "Career Counselling", 
      icon: "bi-briefcase", 
      description: "Navigate career decisions with expert guidance.",
      image: "career.jpg",
      details: {
        overview: "Help with career decisions, professional transitions, and vocational development.",
        focusAreas: [
          "Career transition planning",
          "Skills assessment",
          "Work-life balance",
          "Interview preparation",
          "Professional goal setting"
        ],
        approach: "Assessment-driven approach using career inventories and strategic planning."
      }
    },
    { 
      name: "Academic Guidance", 
      icon: "bi-compass", 
      description: "Academic counseling and educational guidance for students.",
      image: "academic.jpg",
      details: {
        overview: "Support students in achieving educational excellence while managing academic stress.",
        focusAreas: [
          "Study skills and time management",
          "Test anxiety reduction",
          "Academic motivation",
          "Learning strategy development",
          "Educational planning"
        ],
        approach: "Cognitive-behavioral techniques combined with academic skill-building."
      }
    },
    { 
      name: "Stress Management", 
      icon: "bi-cloud-sun", 
      description: "Evidence-based tools to reduce stress and build resilience.",
      image: "stress.jpg",
      details: {
        overview: "Therapeutic strategies to manage stress, anxiety, and improve well-being.",
        focusAreas: [
          "Anxiety reduction techniques",
          "Mindfulness practices",
          "Time management",
          "Relaxation strategies",
          "Lifestyle balance"
        ],
        approach: "Mindfulness-Based Stress Reduction (MBSR) with cognitive-behavioral techniques."
      }
    },
    { 
      name: "Grief Counseling", 
      icon: "bi-flower1", 
      description: "Grief therapy and bereavement support for loss processing.",
      image: "grief.jpg",
      details: {
        overview: "Compassionate support through the grieving process to help individuals navigate loss.",
        focusAreas: [
          "Bereavement processing",
          "Complicated grief resolution",
          "Life meaning reconstruction",
          "Multiple loss coping",
          "Anticipatory grief management"
        ],
        approach: "Meaning reconstruction therapy within a grief-informed framework."
      }
    }
  ];

  const handleLearnMore = (service) => {
    setSelectedService(service);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setSelectedService(null);
    document.body.style.overflow = "auto";
  };

  const handleScheduleConsultation = (serviceName = "General Consultation") => {
    navigate("/contact", { 
      state: { 
        scrollToBooking: true,
        selectedService: serviceName
      } 
    });
    closeModal();
  };

  return (
    <div className="services-page">
      {/* Hero Banner */}
      <section className="services-hero">
        <div className="container">
          <div className="hero-content"><br></br>
            <h1 className="hero-title">
              Professional Counselling Services in <span className="highlight">Nagercoil</span>
            </h1>
            <p className="hero-subtitle">
              Expert mental health care provided by certified professionals. Comprehensive counseling for individuals, couples, families in Tamil Nadu.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">15+</span>
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">1000+</span>
                <span className="stat-label">Satisfied Clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-grid-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Counselling for Psychological Issues</h2>
            <p className="section-subtitle">
              We provide offline & online counselling/therapy for depression, anxiety, stress, relationship issues, and career concerns.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card" onClick={() => handleLearnMore(service)}>
                <div className="card-icon">
                  <i className={`bi ${service.icon}`}></i>
                </div>
                <h3 className="card-title">{service.name}</h3>
                <p className="card-description">{service.description}</p>
                <button className="card-button">
                  Know More <i className="bi bi-arrow-right"></i>
                </button>
              </div>
            ))}
          </div>

          <div className="cta-section">
            <h3>Ready to Begin Your Healing Journey?</h3>
            <p>Schedule a confidential consultation with our certified expert.</p>
            <button className="cta-button" onClick={() => handleScheduleConsultation()}>
              <i className="bi bi-calendar-check"></i>
              Book a Session
            </button>
          </div>
        </div>
      </section>

      {/* Simple Square Modal */}
      {selectedService && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="simple-modal" onClick={(e) => e.stopPropagation()}>
            {/* X Button at Top Right */}
            <button className="modal-close-x" onClick={closeModal}>
              <i className="bi bi-x"></i>
            </button>

            {/* Modal Content */}
            <div className="modal-content">
              <div className="modal-header">
                <h2>{selectedService.name}</h2>
              </div>

              <div className="modal-body">
                <div className="modal-section">
                  <h3>Service Overview</h3>
                  <p>{selectedService.details.overview}</p>
                </div>

                <div className="modal-section">
                  <h3>Focus Areas</h3>
                  <div className="focus-list">
                    {selectedService.details.focusAreas.map((area, index) => (
                      <div key={index} className="focus-item">
                        <i className="bi bi-check"></i>
                        <span>{area}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-section">
                  <h3>Therapeutic Approach</h3>
                  <p>{selectedService.details.approach}</p>
                </div>
              </div>

              {/* Buttons at Bottom */}
              <div className="modal-buttons">
                <button 
                  className="primary-btn"
                  onClick={() => handleScheduleConsultation(selectedService.name)}
                >
                  <i className="bi bi-calendar-check"></i>
                  Book This Service
                </button>
                <button className="secondary-btn" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;